import {useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';

import {APIError} from 'data/errors';
import {HttpStatusCode} from 'data/protocols/http';
import {setLoginToken} from 'infra/http/utils';
import {logEvent} from 'main/analytics/analytics';
import {LogEventLoginEnum} from 'main/analytics/logEventEnum';
import {
	ForgotPasswordEnum,
	OperationStateType,
	OperationStateTypesEnum,
	SelectionFlowEnum,
} from 'presentation/constants';
import {useHostName} from 'presentation/hooks/useHostName';
import {IFormData} from 'presentation/pages/public/sign-in/components/SignInForm/schema';
import {translations} from 'presentation/translations-new';
import {parseJwt} from 'presentation/utils';
import {extractLinkFromMessage} from 'presentation/utils/extractLinkFromMessage';
import {getLoginIdentifier} from 'presentation/utils/masks';
import {useAccountDataStore} from 'presentation/pages/private/store/useAccountDataStateStore';
import {useSetRecoilState} from 'recoil';
import {accountDataStateRefresher} from 'presentation/pages/states';
import {useRemoteAuthenticationMutation} from '../../mutation/useRemoteAuthenticationMutation';
import {usePortalRedirectLinkStore} from '../../store/usePortalRedirectLinkStore';
import {MAX_LOGIN_ATTEMPTS} from './constants';
import {UseLoginProps} from './types';

const {serverError} = translations['pt-br'].commons;
const {invalidUserOrPassword} = translations['pt-br'].publicFlow.APIErrors;

export function useLogin({methods}: UseLoginProps) {
	const {dataHostNameState} = useHostName();
	const navigate = useNavigate();
	const location = useLocation();
	const {
		getValues,
		formState: {isValidating},
	} = methods;

	const [requestState, setRequestState] = useState<OperationStateType | null>(null);
	const [loginAttemptsCountState, setLoginAttemptsCountState] = useState(1);
	const [apiErrorMsg, setApiErrorMsg] = useState('');
	const portalRedirectLink = usePortalRedirectLinkStore(state => state.portalRedirectLink);
	const setPortalRedirectLink = usePortalRedirectLinkStore(state => state.setPortalRedirectLink);
	const resetPortalRedirectLink = usePortalRedirectLinkStore(
		state => state.resetPortalRedirectLink,
	);
	const setAccountData = useAccountDataStore(state => state.setAccountData);
	const setAccountDataStateRefresher = useSetRecoilState(accountDataStateRefresher);

	const formFieldValues = getValues();
	const isDirty = Object.values(formFieldValues).every(val => val !== '');
	const showAPIError =
		requestState === OperationStateTypesEnum.ERROR && isDirty && !portalRedirectLink;
	const [previousFormDataState, setPreviousFormDataState] = useState<IFormData | null>(null);

	const currentFormData = getValues();

	const {mutateAsync: authentication} = useRemoteAuthenticationMutation();

	const isSameCredentials = (current: IFormData, previous: IFormData | null): boolean => {
		if (!previous) return false;
		return current.login === previous.login && current.password === previous.password;
	};

	const resetLoginAttempts = (formData: IFormData): void => {
		setPreviousFormDataState(formData);
		setLoginAttemptsCountState(1);
	};

	const redirectToForgotPassword = (): void => {
		navigate(ForgotPasswordEnum.FORGOT_PASSWORD, {
			state: {
				maxLoginAttempts: true,
			},
		});
	};

	const handleFailedLoginAttempt = (): void => {
		const areSameCredentials = isSameCredentials(currentFormData, previousFormDataState);

		if (!areSameCredentials) {
			resetLoginAttempts(currentFormData);
			return;
		}

		const newAttemptCount = loginAttemptsCountState + 1;
		setLoginAttemptsCountState(newAttemptCount);

		if (newAttemptCount >= MAX_LOGIN_ATTEMPTS) {
			redirectToForgotPassword();
		}
	};

	const getLoggedUser = async (token: string) => {
		if (!token) {
			return;
		}

		const parsedToken = parseJwt(token);

		const {cdPessoa, cdUsuario} = parsedToken.data;

		setAccountData({
			logged_user: {
				cdPessoa,
				cdUsuario,
			},
		});
		setAccountDataStateRefresher(val => val + 1);
	};

	const handleLogin = async (data: IFormData) => {
		try {
			setRequestState(OperationStateTypesEnum.PENDING);
			setPortalRedirectLink('');

			const identifier = getLoginIdentifier(data.login);
			if (identifier === '0') {
				setRequestState(OperationStateTypesEnum.ERROR);
				setApiErrorMsg(invalidUserOrPassword);
				return;
			}

			const {login_token, flRedefinicaoSenha} = await authentication({
				cdEmpresaPlano: dataHostNameState?.productCode,
				identificador: identifier,
				senha: data.password,
				usuario: data.login,
			});

			setLoginToken(login_token);
			getLoggedUser(login_token);
			setRequestState(OperationStateTypesEnum.DONE);

			logEvent({
				eventName: LogEventLoginEnum.PORTAL_LOGIN_SUCCESS,
				operator: dataHostNameState?.productCode,
			});

			if (flRedefinicaoSenha) {
				navigate(ForgotPasswordEnum.FORGOT_PASSWORD);
			} else {
				navigate(SelectionFlowEnum.PLAN_SELECTION, {
					state: {
						isLogin: true,
						...location.state,
					},
				});
			}
		} catch (error) {
			logEvent({
				eventName: LogEventLoginEnum.PORTAL_LOGIN_INVALID_CREDENTIALS,
				operator: dataHostNameState?.productCode,
			});
			if (error instanceof APIError) {
				setRequestState(OperationStateTypesEnum.ERROR);
				setApiErrorMsg(error.message);

				if (error.statusCode === HttpStatusCode.unprocessable) {
					const redirectLinkValue = extractLinkFromMessage(error.message);
					if (redirectLinkValue) {
						setPortalRedirectLink(redirectLinkValue);
						return;
					}
					setApiErrorMsg(invalidUserOrPassword);
					handleFailedLoginAttempt();
				}

				if (error.statusCode === HttpStatusCode.serverError) {
					setApiErrorMsg(serverError);
				}
			}
		}
	};

	const clearAPIError = () => {
		setRequestState(null);
		resetPortalRedirectLink();
	};

	useEffect(() => {
		if (isValidating) {
			clearAPIError();
		}
	}, [isValidating]);

	return {
		handleLogin,
		apiErrorMsg,
		showAPIError,
		portalRedirectLink,
	};
}
